import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  fr: {
    nav: {
      home: 'Accueil',
      process: 'Parcours',
      faq: 'FAQ',
      contact: 'Contact',
      bookCall: 'Planifier une consultation',
      callNow: '438-817-7277',
    },
    hero: {
      line1: "L'achat d'un véhicule neuf n'est pas une transaction.",
      line2: "C'est une décision.",
      impacts: [
        'Elle impacte votre temps.',
        'Votre capital.',
        'Votre marge de manœuvre.',
      ],
      conclusion: 'Nous la structurons.',
      arrow: 'De l\'analyse initiale jusqu\'à la livraison de votre véhicule neuf et au suivi.',
      cta: 'Planifier une consultation stratégique',
      phone: '438-817-7277',
    },
    analyse: {
      p1: 'Vous arrivez avec une idée.',
      p2: 'Nous la remettons en question.',
      p3: 'Pas pour contredire.',
      p4: 'Pour éviter l\'erreur.',
      risks: [
        'Choix émotionnel.',
        'Marque peu fiable.',
        'Structure financière risquée.',
      ],
      p5: 'On met tout sur la table.',
      emphasis: 'Parce qu\'un mauvais choix automobile se paie pendant des années.',
      role: 'Notre rôle :',
      actions: ['valider', 'corriger', 'sécuriser'],
      closing: 'Avant que vous engagiez votre capital.',
    },
    selection: {
      p1: 'Le marché du véhicule neuf est complexe.',
      p2: 'Nous le simplifions.',
      p3: 'Chaque véhicule neuf recommandé est analysé, comparé et présenté de façon claire.',
      understand: 'Vous comprenez immédiatement :',
      points: [
        'ce que vous achetez',
        'pourquoi vous l\'achetez',
        'et si c\'est réellement le bon choix',
      ],
      p4: 'Vous n\'avez pas besoin de "connaître les autos".',
      p5: 'Nous rendons chaque décision compréhensible.',
      closing: ['Sans jargon.', 'Sans pression.', 'Sans zone grise.'],
      final: 'Vous ne serez jamais laissé dans l\'incompréhension.',
    },
    essais: {
      p1: 'Un showroom n\'est pas la réalité.',
      p2: 'Votre quotidien l\'est.',
      p3: 'Nous organisons des essais directement chez vous.',
      p4: 'Jusqu\'à deux véhicules.',
      p5: 'Vous comparez dans votre environnement réel.',
      closing: ['Sans pression.', 'Sans vendeur.', 'Sans déplacement.'],
    },
    negociation: {
      p1: 'Un acheteur négocie rarement.',
      p2: 'Nous négocions tous les jours.',
      p3: 'Avec plus de 25M$ en volume annuel, la dynamique change.',
      p4: 'Vous arrivez seul.',
      p5: 'Nous arrivons avec un levier.',
      p6: 'Ce n\'est pas une discussion.',
      p7: 'C\'est une position.',
    },
    coordination: {
      p1: 'Vous n\'avez rien à gérer.',
      p2: 'Nous prenons en charge :',
      items: ['les échanges', 'les validations', 'les conditions', 'la livraison'],
      p3: 'Chaque étape est contrôlée.',
      p4: 'Sans friction.',
    },
    livraison: {
      p1: 'La livraison n\'est pas la fin.',
      p2: 'C\'est le début.',
      p3: '7 à 10 jours plus tard, nous revenons vers vous.',
      p4: 'À domicile.',
      p5: 'Parce que le jour de la livraison, tout va trop vite.',
      p6: 'Nous prenons le temps de :',
      items: [
        'configurer votre véhicule',
        'adapter chaque fonctionnalité à votre usage',
        'répondre à toutes vos questions',
      ],
      p7: 'Nous prenons le temps de vous rendre entièrement à l\'aise.',
      p8: 'Aucune question n\'est trop simple.',
    },
    philosophie: {
      line1: 'Un bon service ne se mesure pas en promesses.',
      line2: 'Il se mesure en erreurs évitées.',
    },
    stats: {
      items: [
        { value: '13+', label: 'Années d\'expérience' },
        { value: '1 000+', label: 'Clients accompagnés' },
        { value: '25M$', label: 'Volume négocié' },
        { value: '4.9', label: 'Note Google' },
      ],
    },
    faq: {
      title: 'Questions.',
      items: [
        {
          question: 'Quelle est la différence?',
          answer: 'Les autres facilitent l\'achat.\n\nNous structurons la décision.\n\nAnalyse, sélection, négociation, exécution.',
        },
        {
          question: 'Travaillez-vous avec toutes les marques?',
          answer: 'Nous avons accès à l\'ensemble du marché.\n\nNotre rôle n\'est pas de vous diriger vers une marque.\n\nNotre rôle est de vous orienter vers la bonne décision.',
        },
        {
          question: 'Combien de temps?',
          answer: '2 à 4 semaines.\n\nLe temps nécessaire pour analyser, structurer et exécuter correctement.',
        },
        {
          question: 'Quelle zone couvrez-vous?',
          answer: 'Grand Montréal, Laval, Rive-Nord, Rive-Sud.',
        },
        {
          question: 'Pourquoi vous faire confiance?',
          answer: 'Parce que nous ne dépendons de personne.\n\nEt que chaque décision est prise dans votre intérêt.',
        },
      ],
    },
    forWho: {
      title: 'Ce service n\'est pas pour tout le monde.',
      items: [
        {
          statement: 'Si vous cherchez simplement le prix le plus bas',
          reveal: 'ce n\'est pas ici',
        },
        {
          statement: 'Si vous aimez négocier vous-même',
          reveal: 'ce n\'est pas ici',
        },
        {
          statement: 'Si vous voulez une décision claire, structurée et sécurisée',
          reveal: 'vous êtes au bon endroit',
          positive: true,
        },
      ],
    },
    confiance: {
      line1: 'Un bon accompagnement ne consiste pas à parler plus fort.',
      line2: 'Il consiste à rendre les choses claires.',
    },
    cta: {
      title: 'Planifier une consultation stratégique.',
      subtitle: '15 minutes. On valide si on peut créer de la valeur pour vous.',
      button: 'Planifier une consultation',
      phone: '438-817-7277',
    },
    contact: {
      overline: 'Contact',
      title: 'Parlons de votre projet.',
      subtitle: 'Remplissez le formulaire. Un conseiller vous contacte sous 24h.',
      form: {
        name: 'Nom complet',
        email: 'Courriel',
        phone: 'Téléphone',
        message: 'Votre message (optionnel)',
        vehicleType: 'Type de véhicule recherché',
        budget: 'Budget approximatif',
        submit: 'Envoyer ma demande',
        sending: 'Envoi en cours...',
        success: 'Merci. Nous vous contacterons sous 24h.',
        error: 'Une erreur s\'est produite. Veuillez réessayer.',
      },
      info: {
        phone: '438-817-7277',
        email: 'info@consultauto.ca',
        address: 'Grand Montréal, Québec',
        hours: 'Lun-Ven: 9h-18h | Sam: Sur rendez-vous',
      },
    },
    footer: {
      description: 'Firme de conseil automobile indépendante. Spécialiste de l\'achat et la location de véhicule neuf. Vos intérêts. Pas ceux des concessionnaires.',
      company: 'Navigation',
      privacy: 'Confidentialité',
      terms: 'Conditions',
      copyright: '© 2025 Consultauto. Tous droits réservés.',
    },
    about: {
      overline: 'Notre histoire',
      title: 'À propos de Consultauto',
      subtitle: 'Depuis 13 ans, nous structurons les acquisitions automobiles au Québec.',
      mission: {
        title: 'Notre mission',
        description: 'Permettre à chaque client d\'acquérir son véhicule avec clarté, structure et les meilleures conditions possibles.',
      },
      values: {
        title: 'Nos principes',
        items: [
          { title: 'Indépendance', description: 'Votre intérêt. Toujours.' },
          { title: 'Rigueur', description: 'Chaque décision est structurée.' },
          { title: 'Transparence', description: 'Zéro zone grise.' },
        ],
      },
    },
  },
  en: {
    nav: {
      home: 'Home',
      process: 'Process',
      faq: 'FAQ',
      contact: 'Contact',
      bookCall: 'Book a consultation',
      callNow: '438-817-7277',
    },
    hero: {
      line1: "Buying a new vehicle is not a transaction.",
      line2: "It's a decision.",
      impacts: [
        'It impacts your time.',
        'Your capital.',
        'Your margin of maneuver.',
      ],
      conclusion: 'We structure it.',
      arrow: 'From initial analysis to new vehicle delivery and follow-up.',
      cta: 'Schedule a strategic consultation',
      phone: '438-817-7277',
    },
    analyse: {
      p1: 'You come in with an idea.',
      p2: 'We challenge it.',
      p3: 'Not to contradict.',
      p4: 'To prevent the mistake.',
      risks: [
        'Emotional choice.',
        'Unreliable brand.',
        'Risky financial structure.',
      ],
      p5: 'We put everything on the table.',
      emphasis: 'Because a bad vehicle decision is paid for over years.',
      role: 'Our role:',
      actions: ['validate', 'correct', 'secure'],
      closing: 'Before you commit your capital.',
    },
    selection: {
      p1: 'The market is complex.',
      p2: 'We simplify it.',
      p3: 'Every recommended vehicle is analyzed, compared and presented clearly.',
      understand: 'You immediately understand:',
      points: [
        'what you\'re buying',
        'why you\'re buying it',
        'and if it\'s really the right choice',
      ],
      p4: 'You don\'t need to "know cars".',
      p5: 'We make every decision understandable.',
      closing: ['No jargon.', 'No pressure.', 'No grey areas.'],
      final: 'You will never be left in the dark.',
    },
    essais: {
      p1: 'A showroom is not reality.',
      p2: 'Your daily life is.',
      p3: 'We organize test drives directly at your home.',
      p4: 'Up to two vehicles.',
      p5: 'You compare in your real environment.',
      closing: ['No pressure.', 'No salesperson.', 'No travel.'],
    },
    negociation: {
      p1: 'A buyer rarely negotiates.',
      p2: 'We negotiate every day.',
      p3: 'With over $25M in annual volume, the dynamic changes.',
      p4: 'You show up alone.',
      p5: 'We show up with leverage.',
      p6: 'This is not a discussion.',
      p7: 'It\'s a position.',
    },
    coordination: {
      p1: 'You have nothing to manage.',
      p2: 'We handle:',
      items: ['communications', 'validations', 'conditions', 'delivery'],
      p3: 'Every step is controlled.',
      p4: 'Without friction.',
    },
    livraison: {
      p1: 'Delivery is not the end.',
      p2: 'It\'s the beginning.',
      p3: '7 to 10 days later, we come back to you.',
      p4: 'At your home.',
      p5: 'Because on delivery day, everything moves too fast.',
      p6: 'We take the time to:',
      items: [
        'configure your vehicle',
        'adapt each feature to your usage',
        'answer all your questions',
      ],
      p7: 'We take the time to make you completely comfortable.',
      p8: 'No question is too simple.',
    },
    philosophie: {
      line1: 'A good service is not measured in promises.',
      line2: 'It\'s measured in mistakes avoided.',
    },
    stats: {
      items: [
        { value: '13+', label: 'Years of experience' },
        { value: '1,000+', label: 'Clients served' },
        { value: '$25M', label: 'Volume negotiated' },
        { value: '4.9', label: 'Google rating' },
      ],
    },
    faq: {
      title: 'Questions.',
      items: [
        {
          question: 'What\'s the difference?',
          answer: 'Others facilitate the purchase.\n\nWe structure the decision.\n\nAnalysis, selection, negotiation, execution.',
        },
        {
          question: 'Do you work with all brands?',
          answer: 'We have access to the entire market.\n\nOur role is not to direct you toward a brand.\n\nOur role is to guide you toward the right decision.',
        },
        {
          question: 'How long does it take?',
          answer: '2 to 4 weeks.\n\nThe time needed to analyze, structure and execute properly.',
        },
        {
          question: 'What area do you cover?',
          answer: 'Greater Montreal, Laval, North Shore, South Shore.',
        },
        {
          question: 'Why trust you?',
          answer: 'Because we depend on no one.\n\nAnd every decision is made in your interest.',
        },
      ],
    },
    forWho: {
      title: 'This service is not for everyone.',
      items: [
        {
          statement: 'If you\'re simply looking for the lowest price',
          reveal: 'this is not the place',
        },
        {
          statement: 'If you enjoy negotiating yourself',
          reveal: 'this is not the place',
        },
        {
          statement: 'If you want a clear, structured and secure decision',
          reveal: 'you\'re in the right place',
          positive: true,
        },
      ],
    },
    confiance: {
      line1: 'Good support is not about speaking louder.',
      line2: 'It\'s about making things clear.',
    },
    cta: {
      title: 'Schedule a strategic consultation.',
      subtitle: '15 minutes. We confirm if we can create value for you.',
      button: 'Schedule a consultation',
      phone: '438-817-7277',
    },
    contact: {
      overline: 'Contact',
      title: 'Let\'s talk about your project.',
      subtitle: 'Fill out the form. An advisor will contact you within 24 hours.',
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        message: 'Your message (optional)',
        vehicleType: 'Vehicle type sought',
        budget: 'Approximate budget',
        submit: 'Send my request',
        sending: 'Sending...',
        success: 'Thank you. We will contact you within 24 hours.',
        error: 'An error occurred. Please try again.',
      },
      info: {
        phone: '438-817-7277',
        email: 'info@consultauto.ca',
        address: 'Greater Montreal, Quebec',
        hours: 'Mon-Fri: 9am-6pm | Sat: By appointment',
      },
    },
    footer: {
      description: 'Independent automotive consulting firm. Specialists in new vehicle purchase and lease. Your interests. Not the dealerships\'.',
      company: 'Navigation',
      privacy: 'Privacy',
      terms: 'Terms',
      copyright: '© 2025 Consultauto. All rights reserved.',
    },
    about: {
      overline: 'Our story',
      title: 'About Consultauto',
      subtitle: 'For 13 years, we\'ve been structuring automotive acquisitions in Quebec.',
      mission: {
        title: 'Our mission',
        description: 'To allow every client to acquire their vehicle with clarity, structure and the best possible conditions.',
      },
      values: {
        title: 'Our principles',
        items: [
          { title: 'Independence', description: 'Your interest. Always.' },
          { title: 'Rigor', description: 'Every decision is structured.' },
          { title: 'Transparency', description: 'Zero grey areas.' },
        ],
      },
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    const savedLang = localStorage.getItem('consultauto-language');
    if (savedLang && (savedLang === 'fr' || savedLang === 'en')) {
      setLanguage(savedLang);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    localStorage.setItem('consultauto-language', newLang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
